
import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from './useAuth';
import { useToast } from '@chakra-ui/react';
import { apiJson } from '../lib/api';
import { toUserMessage } from '../lib/apiError';

// Define the shape of a cloned transaction as returned by the API
// Ideally this matches the PlaidTransaction type or a subset
export interface ClonedTransaction {
    id: string;
    original_plaid_transaction_id: string;
    account_id?: string | null;
    amount: number;
    date: string;
    name: string;
    merchant: string | null;
    normalized_merchant?: string | null;
    normalizedMerchant?: string | null;
    normalization_status?: string | null;
    normalizationStatus?: string | null;
    normalization_source?: string | null;
    normalizationSource?: string | null;
    account_name: string | null;
    pfc_primary: string | null;
    pfc_detailed: string | null;
    is_modified: boolean;
    check_number: string | null;
}

export interface CategoryOption {
    primary: string;
    detailed: string;
}

/** Filters accepted by GET /api/transactions/snapshot beyond accountIds (which
 * this hook already takes as a constructor arg) and pagination (limit/offset,
 * passed per-call to fetchSnapshotPage). Mirrors backend GetSnapshotDto. */
export interface SnapshotPageFilters {
    startDate?: string;
    endDate?: string;
    search?: string;
    searchMode?: 'merchant' | 'category';
    filterMode?: 'none' | 'checks' | 'zelle' | 'transfer';
    flowFilter?: 'all' | 'income' | 'expense';
    editFilter?: 'all' | 'edited' | 'original';
    sortBy?: 'date' | 'amount' | 'merchant' | 'flow_alpha';
    sortOrder?: 'asc' | 'desc';
}

export interface SnapshotPageOptions extends SnapshotPageFilters {
    limit: number;
    offset: number;
}

export interface SnapshotPageResult {
    transactions: ClonedTransaction[];
    total: number;
    hasMore: boolean;
}

// Matches the backend DTO's cap (GetSnapshotDto.limit @Max(500)) — the largest
// page fetchSnapshotData's "reconstruct everything" loop can request at once.
const MAX_PAGE_SIZE = 500;

export const useSnapshotTransactions = (tenantId?: string, accountIds?: string[]) => {
    const { session } = useAuth();
    // Keep session in a ref so callbacks always use the latest token
    // without being recreated when TOKEN_REFRESHED fires a new object reference.
    const sessionRef = useRef(session);
    useEffect(() => { sessionRef.current = session; });
    const toast = useToast();
    const [loading, setLoading] = useState(false);
    const [snapshotData, setSnapshotData] = useState<ClonedTransaction[]>([]);
    // Identifies the newest fetchSnapshotData run so older ones can stop.
    const fetchRunIdRef = useRef(0);
    const [dataVersion, setDataVersion] = useState(0);
    const [availableCategories, setAvailableCategories] = useState<CategoryOption[]>([]);

    // Paginated result for the table view (see fetchSnapshotPage below) — kept
    // separate from `snapshotData` (the full, unfiltered-by-anything-but-account
    // array the pivot/flow/balance/grouped views still consume in full).
    const [pageResult, setPageResult] = useState<SnapshotPageResult>({
        transactions: [],
        total: 0,
        hasMore: false,
    });
    const [pageLoading, setPageLoading] = useState(false);

    const getHeaders = useCallback(() => {
        if (!sessionRef.current?.access_token || !tenantId) return null;
        return {
            headers: {
                'x-tenant-id': tenantId,
            }
        };
    }, [tenantId]);

    const initSnapshot = useCallback(async () => {
        const config = getHeaders();
        if (!config || !sessionRef.current?.access_token) return;

        setLoading(true);
        try {
            await apiJson(
                '/api/transactions/snapshot/init',
                { method: 'POST', ...config },
                sessionRef.current!.access_token
            );
            toast({ title: 'Snapshot initialized', status: 'success', duration: 2000 });
            // After init, fetch data — show error toast if the load fails
            return await fetchSnapshotData(true);
        } catch (error) {
            console.error('Failed to init snapshot', error);
            toast({ title: 'Failed to initialize snapshot', status: 'error' });
        } finally {
            setLoading(false);
        }
    }, [getHeaders, toast]);

    const resetSnapshot = useCallback(async () => {
        const config = getHeaders();
        if (!config || !sessionRef.current?.access_token) return;

        if (!window.confirm('Are you sure you want to reset the snapshot? All manual edits will be lost.')) {
            return;
        }

        setLoading(true);
        try {
            await apiJson(
                '/api/transactions/snapshot/reset',
                { method: 'POST', ...config },
                sessionRef.current!.access_token
            );
            toast({ title: 'Snapshot reset', status: 'info', duration: 2000 });
            return await fetchSnapshotData(true);
        } catch (error) {
            console.error('Failed to reset snapshot', error);
            toast({ title: 'Failed to reset snapshot', status: 'error' });
        } finally {
            setLoading(false);
        }
    }, [getHeaders, toast]);

    const refreshSnapshot = useCallback(async () => {
        const config = getHeaders();
        if (!config || !sessionRef.current?.access_token) return;

        setLoading(true);
        try {
            const result = await apiJson<{ status: string; added: number }>(
                '/api/transactions/snapshot/refresh',
                { method: 'POST', ...config },
                sessionRef.current!.access_token
            );
            const added = result?.added ?? 0;
            toast({
                title: added > 0
                    ? `Added ${added} new transaction${added === 1 ? '' : 's'}`
                    : 'Scenario already up to date',
                status: added > 0 ? 'success' : 'info',
                duration: 2500,
            });
            return await fetchSnapshotData(true);
        } catch (error) {
            console.error('Failed to refresh snapshot', error);
            toast({ title: 'Failed to refresh scenario', status: 'error' });
        } finally {
            setLoading(false);
        }
    }, [getHeaders, toast]);

    /**
     * Fetches EVERY cloned transaction matching the account filter (looping
     * the paginated endpoint at MAX_PAGE_SIZE per request) and stores the
     * reconstructed full array in `snapshotData`.
     *
     * This is the same shape of "while-loop over a paginated endpoint" the
     * rest of this plan is eliminating — kept, for now, only because
     * TransactionsExplorer's pivot/flow/balance/grouped views for EDITABLE
     * mode still aggregate client-side over the full dataset (no server-side
     * aggregate endpoints for snapshots yet — see
     * docs/plans/TRANSACTIONS_PAGINATION_PLAN.md, Phase 1.4). It is still a
     * real improvement over the pre-pagination endpoint: that one had zero
     * server-side filtering at all. The TABLE view does NOT use this — see
     * fetchSnapshotPage, which fetches exactly one page.
     */
    const fetchSnapshotData = useCallback(async (showErrorToast = false) => {
        const config = getHeaders();
        if (!config || !sessionRef.current?.access_token) return;

        // Supersede any loop still in flight. This walks the whole snapshot
        // one page at a time, so a caller that re-fires it (a date range
        // settling on mount, an account selection changing) used to leave
        // two or three complete loops racing, each issuing the same ~100
        // requests and the slowest one winning the setState.
        const runId = ++fetchRunIdRef.current;
        const superseded = () => fetchRunIdRef.current !== runId;

        setLoading(true);
        try {
            let all: ClonedTransaction[] = [];
            let offset = 0;
            // eslint-disable-next-line no-constant-condition
            while (true) {
                if (superseded()) return;
                const params = new URLSearchParams();
                if (accountIds && accountIds.length > 0) {
                    // Send as comma-separated list (NestJS cannot parse repeated query params properly)
                    params.append('accountIds', accountIds.join(','));
                }
                params.append('limit', String(MAX_PAGE_SIZE));
                params.append('offset', String(offset));

                const page = await apiJson<SnapshotPageResult>(
                    `/api/transactions/snapshot?${params.toString()}`,
                    { method: 'GET', ...config },
                    sessionRef.current!.access_token
                );
                all = all.concat(page.transactions);
                if (!page.hasMore) break;
                offset += page.transactions.length;
            }

            if (superseded()) return;
            setSnapshotData(all);
            setDataVersion(v => v + 1);
            return all;
        } catch (error) {
            if (superseded()) return;
            console.error('Failed to fetch snapshot data', error);
            if (showErrorToast) {
                toast({
                    title: 'Error loading snapshot data',
                    description: toUserMessage(error, 'Please try again in a moment.'),
                    status: 'error',
                    duration: 6000,
                    isClosable: true,
                });
            }
        } finally {
            if (fetchRunIdRef.current === runId) setLoading(false);
        }
    }, [getHeaders, accountIds, toast]);

    /**
     * Fetches exactly one page of the active snapshot, filtered/sorted
     * server-side. Used by TransactionsExplorer's table view for EDITABLE
     * mode — the actual pagination fix, as opposed to fetchSnapshotData's
     * "loop until everything is loaded" fallback for the aggregate views.
     */
    const fetchSnapshotPage = useCallback(async (options: SnapshotPageOptions) => {
        const config = getHeaders();
        if (!config || !sessionRef.current?.access_token) return;

        setPageLoading(true);
        try {
            const params = new URLSearchParams();
            if (accountIds && accountIds.length > 0) {
                params.append('accountIds', accountIds.join(','));
            }
            if (options.startDate) params.append('startDate', options.startDate);
            if (options.endDate) params.append('endDate', options.endDate);
            if (options.search) params.append('search', options.search);
            if (options.searchMode) params.append('searchMode', options.searchMode);
            if (options.filterMode && options.filterMode !== 'none') {
                params.append('filterMode', options.filterMode);
            }
            if (options.flowFilter && options.flowFilter !== 'all') {
                params.append('flowFilter', options.flowFilter);
            }
            if (options.editFilter && options.editFilter !== 'all') {
                params.append('editFilter', options.editFilter);
            }
            if (options.sortBy) params.append('sortBy', options.sortBy);
            if (options.sortOrder) params.append('sortOrder', options.sortOrder);
            params.append('limit', String(options.limit));
            params.append('offset', String(options.offset));

            const result = await apiJson<SnapshotPageResult>(
                `/api/transactions/snapshot?${params.toString()}`,
                { method: 'GET', ...config },
                sessionRef.current!.access_token
            );
            setPageResult(result);
            return result;
        } catch (error) {
            console.error('Failed to fetch snapshot page', error);
            toast({
                title: 'Error loading transactions',
                description: toUserMessage(error, 'Please try again in a moment.'),
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setPageLoading(false);
        }
    }, [getHeaders, accountIds, toast]);

    const updateTransaction = useCallback(async (id: string, changes: { pfc_primary?: string, pfc_detailed?: string, merchant?: string }) => {
        const config = getHeaders();
        if (!config || !sessionRef.current?.access_token) return;

        try {
            await apiJson(
                `/api/transactions/snapshot/${id}`,
                { 
                    method: 'PATCH', 
                    body: JSON.stringify(changes),
                    ...config 
                },
                sessionRef.current!.access_token
            );
            
            // Optimistic update
            setSnapshotData(prev => prev.map(t => 
                t.id === id ? { ...t, ...changes, is_modified: true } : t
            ));
            setDataVersion(v => v + 1);
            
            toast({ title: 'Transaction updated', status: 'success', duration: 1000 });
        } catch (error) {
            console.error('Failed to update transaction', error);
            toast({ title: 'Failed to update transaction', status: 'error' });
            // Revert changes? For now just reload
            fetchSnapshotData();
        }
    }, [getHeaders, fetchSnapshotData, toast]);

    const fetchCategories = useCallback(async () => {
        const config = getHeaders();
        if (!config || !sessionRef.current?.access_token) return;

        try {
            const categories = await apiJson<CategoryOption[]>(
                '/api/transactions/snapshot/categories',
                { method: 'GET', ...config },
                sessionRef.current!.access_token
            );
            setAvailableCategories(categories);
            return categories;
        } catch (error) {
            console.error('Failed to fetch categories', error);
        }
    }, [getHeaders]);

    const revertTransaction = useCallback(async (id: string) => {
        const config = getHeaders();
        if (!config || !sessionRef.current?.access_token) return;

        try {
            const reverted = await apiJson<ClonedTransaction>(
                `/api/transactions/snapshot/${id}/revert`,
                { method: 'POST', ...config },
                sessionRef.current!.access_token
            );

            // Update local state with reverted data
            setSnapshotData(prev => prev.map(t =>
                t.id === id ? { ...t, ...reverted, is_modified: false } : t
            ));
            setDataVersion(v => v + 1);

            toast({ title: 'Transaction reverted', status: 'info', duration: 1500 });
        } catch (error) {
            console.error('Failed to revert transaction', error);
            toast({ title: 'Failed to revert transaction', status: 'error' });
            fetchSnapshotData();
        }
    }, [getHeaders, fetchSnapshotData, toast]);

    const bulkUpdateTransactions = useCallback(async (ids: string[], changes: { pfc_primary?: string, pfc_detailed?: string, merchant?: string, normalized_merchant?: string }) => {
        const config = getHeaders();
        if (!config || !sessionRef.current?.access_token) return;

        try {
            await apiJson(
                '/api/transactions/snapshot/bulk',
                { 
                    method: 'PATCH', 
                    body: JSON.stringify({ ids, updates: changes }),
                    ...config 
                },
                sessionRef.current!.access_token
            );
            
            // Optimistic update
            setSnapshotData(prev => prev.map(t => 
                ids.includes(t.id) ? { ...t, ...changes, is_modified: true } : t
            ));
            setDataVersion(v => v + 1);
            
            toast({ title: 'Bulk update successful', status: 'success', duration: 1000 });
        } catch (error) {
            console.error('Failed to bulk update transactions', error);
            toast({ title: 'Failed to bulk update', status: 'error' });
            fetchSnapshotData();
        }
    }, [getHeaders, fetchSnapshotData, toast]);

    return {
        loading,
        snapshotData,
        dataVersion,
        availableCategories,
        initSnapshot,
        resetSnapshot,
        refreshSnapshot,
        fetchSnapshotData,
        fetchCategories,
        updateTransaction,
        revertTransaction,
        bulkUpdateTransactions,
        // Paginated table-view API
        pageResult,
        pageLoading,
        fetchSnapshotPage,
    };
};
